import { useState, useEffect } from "react";
import { IoCheckmarkDoneSharp, IoCloseSharp, IoTrash } from "react-icons/io5";
import { HiBell, HiSparkles } from "react-icons/hi2";
import SkeletonGeneralCard from "../Shared/Skeletons/SkeletonGeneralCard";
import { classNames, dateToLocal, dateToLocalHour } from "src/helpers/mixins";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import APIService from "src/services/Api";
import { ImSpinner9 } from "react-icons/im";
import { INotification } from "src/types";
import { useAppStateContext } from "src/auth/AppStateContext";
import { isMobileDevice } from "src/helpers/animationUtils";

type ParamsNotiUB = {
  close(): void;
};
type TypeLoadingDel = {
  id: number | string;
  loading: boolean;
};

const NotificationUserBody: React.FC<ParamsNotiUB> = ({ close }) => {
  const navigate = useNavigate();
  const [animateClose, setAnimateClose] = useState<boolean>(false);
  const [loadingDel, setLoadingDel] = useState<TypeLoadingDel>({ id: "", loading: false });
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const { notifications: notification } = useAppStateContext();
  const [animationDelete, setAnimationDelete] = useState<string | number>("-");
  const isMobile = isMobileDevice()
  const handleClose = () => {
    setAnimateClose(true);
    window.setTimeout(() => {
      close();
    }, isMobile ? 200 : 500); // Reducir tiempo en móvil
  };
  useEffect(() => {
    if (notification !== notifications) {
      setNotifications(notification);
    }
  }, [notification, notifications]);

  const handleRemove = async (id: string | number) => {
    setLoadingDel({ id, loading: true });
    const del = await APIService.userNotificationDelete({ id });
    console.log(del);
    if (del.data.data.del.affected === 1) {
      setAnimationDelete(id);
      setNotifications(notifications.filter((e) => e.id !== id));
    } else {
      alert("no fue posible eliminar");
    }
    setLoadingDel({ id, loading: false });
  };
  /*const markAsRead = async(id:any) => {
     await APIService.userNotificationRead({id})
    if(!notifications.find(n => n.id === id)?.seen){
      setNotifications(notifications.map(e => {
        if(e.id === id){
          e.seen = true
        }
        return e
      }))
    } 
  }*/
  const handleNavigate = async (link: string, id: any) => {
    //markAsRead(id)
    handleClose();
    navigate(link);
  };

  return (
    <div>
      <div className="sidebar-notification-user opensans">
        <div className={classNames("opacity-before", animateClose ? "fadeout" : "fadein")}></div>
        <div
          className={classNames(`content-notification-user bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900`, animateClose ? `forwardleft` : `forwardright`)}
        >
          <div className="bg-gradient-to-r from-emerald-600 via-emerald-700 to-emerald-800 text-white flex items-center justify-between py-5 px-6 shadow-2xl border-b border-emerald-900/50">
            <div className={`flex items-center gap-3 ${isMobile ? 'fadeintop' : 'fadeintop'}`}>
              <div className="relative">
                <div className="absolute inset-0 bg-white/20 rounded-full blur-sm"></div>
                <div className="relative bg-white/10 p-2 rounded-xl backdrop-blur-sm border border-white/20">
                  <HiBell className="text-white" size={24} />
                </div>
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Notificaciones</h2>
                <p className="text-xs text-emerald-100 font-medium">
                  {notifications.filter((n) => !n.seen).length > 0
                    ? `${notifications.filter((n) => !n.seen).length} nueva${notifications.filter((n) => !n.seen).length > 1 ? 's' : ''}`
                    : 'Todo al día'}
                </p>
              </div>
            </div>
            <button
              onClick={() => handleClose()}
              className="p-2 rounded-lg hover:bg-white/20 transition-all duration-200 cursor-pointer group"
            >
              <IoCloseSharp className="text-white group-hover:rotate-90 transition-transform duration-200" size={24} />
            </button>
          </div>
          <div className="px-0 pt-0 pb-4 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900">
            <SkeletonGeneralCard
              loading={false}
              heightClass={"h-52"}
              arrayItems={8}
              mtClass="mt-0"
              className={"grid grid-cols-1 gap-4"}
            >
              <section className="overflow-x-auto" style={{ maxHeight: "85vh" }}>
                {notifications.length === 0 && (
                  <div className="bg-gray-800/50 rounded-xl shadow-lg px-6 py-8 mb-4 border border-gray-700/50 text-center backdrop-blur-sm">
                    <div className="flex justify-center mb-4">
                      <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/30 p-4 rounded-full border border-emerald-500/30">
                        <HiBell className="text-emerald-400" size={32} />
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-100 mb-2">Todo está al día</h3>
                    <p className="text-sm text-gray-400 leading-relaxed">
                      Cuando haya algún movimiento, mensajes o clases, te avisaremos
                      inmediatamente.
                    </p>
                  </div>
                )}
                <div className="px-4 py-4">
                  {notifications.map((n, key) => (
                    <div
                      key={key.toString()}
                      className={classNames(
                        animationDelete === n.id ? (isMobile ? "" : "fadeout") : "",
                        n.seen 
                          ? "bg-gray-800/60 border-gray-700/50" 
                          : "bg-gradient-to-r from-emerald-900/40 to-emerald-800/30 border-l-4 border-emerald-500 shadow-lg",
                        "rounded-xl w-full p-4 mb-4 border transition-all duration-200 hover:shadow-xl hover:scale-[1.01] hover:bg-gray-800/80 backdrop-blur-sm"
                      )}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {!n.seen && (
                            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse shadow-lg shadow-emerald-500/50"></div>
                          )}
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              {moment(new Date()).format("YYYY-MM-DD") !== dateToLocal(n.createdAt) ? (
                                <span className="text-xs font-semibold text-gray-300">{dateToLocal(n.createdAt)}</span>
                              ) : (
                                <span className={classNames(
                                  n?.seen 
                                    ? "bg-gray-700/50 text-gray-300" 
                                    : "bg-gradient-to-r from-emerald-500 to-emerald-600 text-white",
                                  "px-2 py-0.5 rounded-full text-xs font-bold shadow-md"
                                )}>
                                  Hoy
                                </span>
                              )}
                              <span className="text-xs text-gray-400 font-medium">
                                {dateToLocalHour(n.createdAt)}
                              </span>
                            </div>
                          </div>
                        </div>
                        {n.seen && (
                          <div className="bg-emerald-500/20 border border-emerald-500/30 p-1.5 rounded-full">
                            <IoCheckmarkDoneSharp size={14} className="text-emerald-400" />
                          </div>
                        )}
                      </div>
                      
                      <div className="noti-minc-header mb-4 text-gray-100 leading-relaxed [&_*]:text-gray-100 [&_*]:text-sm">
                        <span dangerouslySetInnerHTML={{ __html: n.message }} />
                      </div>

                      <div className="flex items-center gap-3 pt-3 border-t border-gray-700/50">
                        {(n?.link?.length || 0) > 0 && (
                          <button
                            onClick={() => handleNavigate(n.link, n.id)}
                            className="flex items-center gap-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-4 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-all duration-200 shadow-lg hover:shadow-xl shadow-emerald-500/30"
                          >
                            Leer Más
                            <HiSparkles size={14} />
                          </button>
                        )}

                        <button
                          onClick={() =>
                            loadingDel.id === n.id && loadingDel.loading ? undefined : handleRemove(n.id)
                          }
                          disabled={loadingDel.id === n.id && loadingDel.loading}
                          className="flex items-center gap-2 bg-gray-700/50 hover:bg-red-900/40 border border-gray-600/50 hover:border-red-500/50 text-gray-300 hover:text-red-300 px-3 py-2 rounded-lg text-xs font-medium cursor-pointer transition-all duration-200 disabled:opacity-50"
                        >
                          {loadingDel.id === n.id && loadingDel.loading ? (
                            <>
                              <ImSpinner9 className="text-red-400 animate-spin" size={14} />
                              <span>Eliminando...</span>
                            </>
                          ) : (
                            <>
                              <IoTrash size={16} />
                              <span>Eliminar</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="w-full mb-52"></div>
              </section>
            </SkeletonGeneralCard>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationUserBody;
